import axios from 'utils/axios';
import { useEffect, useState } from 'react';
import { IOrganizations } from './../types/organizations.interface';
import { IOrganizationId } from 'types/organization';

type HookNotReadyResult = { ready: false };
type HookReadyResult<T extends object> = { ready: true } & T;
type HookResult<T extends object> = HookReadyResult<T> | HookNotReadyResult;

export type UseOrganizationsState = HookResult<{
    organizations: IOrganizations | Array<{ id: number; name: string; super_organization_id?: number | null }>
    super_organizations?: Array<{ id: number; name: string }>
    accessInProgress: boolean
    accessOrganization(organizationID: IOrganizationId, superuser?: boolean): void
    stopAccessInProgress: boolean
    stopOrganizationAccess: () => Promise<void>
    archiveOrganization(organizationID: IOrganizationId): void
    archiveOrganizationInProgress: boolean
    reactivateOrganization(organizationID: IOrganizationId): void
    reactivateOrganizationInProgress: boolean
}>;

export const useOrganizations = (archived: boolean = false, superuser: boolean = false, superAdmin: boolean = false, accessSuperOrganizations = false) => {
    const [state, setState] = useState<UseOrganizationsState>({
        ready: false
    });

    useEffect(() => {
        (async () => {
            var response: any;
            if (superuser) {
              var fetchURL = accessSuperOrganizations
                ? (archived ? '/berryAPI/superuser/archivedSuperOrganizations' : '/berryAPI/superuser/superOrganizations')
                : (archived ? '/berryAPI/superuser/archivedOrganizations' : '/berryAPI/superuser/organizations');
            }
            else if (superAdmin) {
              var fetchURL = archived ? '/berryAPI/superadmin/archivedOrganizations' : '/berryAPI/superadmin/organizations';
            }
            else {
              var fetchURL = '/berryAPI/organizations';
            }
            
            response = await axios.get(fetchURL);

            const isRegistrationList = fetchURL === '/berryAPI/organizations'; //when getting organizations for registration
            const organizations = isRegistrationList ? response.data.organizations ?? [] : response.data;
            const superOrganizations = isRegistrationList ? response.data.super_organizations ?? [] : [];

            // Access (impersonate) an organization
            const accessOrganization = async (organizationID: IOrganizationId, superuser: boolean = false) => {
              const accessURL =
                superuser ?
                (accessSuperOrganizations ?
                  `/berryAPI/superuser/impersonateSuperOrganization/${organizationID}`
                  : `/berryAPI/superuser/impersonateOrganization/${organizationID}`)
                : `/berryAPI/superadmin/accessOrganization/${organizationID}`;

              setState(s => ({ ...s, accessInProgress: true }));
              await axios.post(accessURL);

              setState(s => ({
                ...s,
                accessInProgress: false,
              }));
            }

            // Stop accessing (impersonating) an organization
            const stopOrganizationAccess = async () => {
              setState(s => ({ ...s, stopAccessInProgress: true }));
              await axios.post(`/berryAPI/user/stopOrganizationAccess`);

              setState(s => ({
                ...s,
                stopAccessInProgress: false,
              }));
            }

            const archiveOrganization = async (organizationID: IOrganizationId) => {
              setState(s => ({ ...s, archiveOrganizationInProgress: true }))
              const archiveURL = superAdmin
                ? `/berryAPI/superadmin/organization/${organizationID}`
                : accessSuperOrganizations
                  ? `/berryAPI/superuser/superOrganization/${organizationID}`
                  : `/berryAPI/superuser/organization/${organizationID}`
              await axios.delete(archiveURL)

              const organizationsListResponse = await axios.get(fetchURL)

              setState(s => ({
                ...s,
                archiveOrganizationInProgress: false,
                organizations: organizationsListResponse.data,
              }))
            }
            
            const reactivateOrganization = async (organizationID: IOrganizationId) => {
              setState(s => ({ ...s, reactivateOrganizationInProgress: true }))
              const reactivateURL = superAdmin
                ? `/berryAPI/superadmin/reactivateOrganization/${organizationID}`
                : accessSuperOrganizations
                  ? `/berryAPI/superuser/reactivateSuperOrganization/${organizationID}`
                  : `/berryAPI/superuser/reactivateOrganization/${organizationID}`
              await axios.post(reactivateURL)

              const organizationsListResponse = await axios.get(fetchURL)

              setState(s => ({
                ...s,
                reactivateOrganizationInProgress: false,
                organizations: organizationsListResponse.data,
              }))
            }
            
            setState({
              ready: true,
              organizations,
              super_organizations: superOrganizations,
              accessOrganization,
              accessInProgress: false,
              stopOrganizationAccess,
              stopAccessInProgress: false,
              archiveOrganization,
              archiveOrganizationInProgress: false,
              reactivateOrganization,
              reactivateOrganizationInProgress: false,
            })
        })();
    }, []);

    return state;
};
